import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Brand } from "components/AppBar/Brand";
import { useLogin } from "components/forms/useLogin";
import PasswordFieldControlled from "components/inputs/TextField/PasswordFieldControlled";
import { TextFieldControlled } from "components/inputs/TextField/TextFieldControlled";
import { USER_LOGIN_FORM } from "constants/forms";
import { RESET_PASSWORD, SIGNUP } from "constants/routes";
import { useMessaging } from "contexts/Messaging/useMessagingContext";
import { AppTopics } from "typings/Topics";

import { Form } from "./Form.styled";

export const LoginForm = () => {
  const { t } = useTranslation();

  const { enqueueMessage } = useMessaging();

  const {
    form,
    form: {
      formState: { isValid },
    },
    handleSubmit,
    state: { error, loading },
  } = useLogin();

  useEffect(() => {
    if (error && !loading) {
      enqueueMessage({
        text: t("loginError"),
        variant: "error",
      });
    }
  }, [error, loading]);

  return (
    <FormProvider {...form}>
      <Form id={USER_LOGIN_FORM} onSubmit={handleSubmit}>
        <Stack sx={{ gap: 4 }}>
          <Stack
            sx={{
              alignItems: "center",
              gap: 4,
            }}
          >
            <Brand disableClick sizeMultiplier={1.25} />
            <Typography variant="h4">{t("welcomeBack")}</Typography>
          </Stack>

          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            <TextFieldControlled
              fullWidth
              helperText=" "
              id="email"
              label={t("email")}
              name="email"
              required
              slotProps={{
                formHelperText: {
                  sx: { mx: 0, my: 0.25 },
                },
              }}
            />

            <PasswordFieldControlled
              autoComplete="current-password"
              fullWidth
              helperText=" "
              id="password"
              label={t("password")}
              name="password"
              required
              slotProps={{
                formHelperText: {
                  sx: { mx: 0, my: 0.25 },
                },
              }}
            />
          </Stack>

          <Box sx={{ px: 2 }}>
            <Button
              data-testid="login-form-submit-button"
              disabled={loading || !isValid}
              fullWidth
              size="medium"
              type="submit"
              variant="contained"
            >
              {t("login")}
            </Button>
          </Box>

          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Stack
              sx={{
                alignItems: "center",
                flexDirection: "row",
                gap: 0.5,
                justifyContent: "center",
              }}
            >
              <Typography variant="body1">{t("forgotPassword")}</Typography>
              <Button
                onClick={() =>
                  PubSub.publish(AppTopics.NavigationRequested, RESET_PASSWORD)
                }
                size="small"
                tabIndex={-1}
                variant="text"
              >
                {t("reset")}
              </Button>
            </Stack>

            <Stack
              sx={{
                alignItems: "center",
                flexDirection: "row",
                gap: 0.5,
                justifyContent: "center",
              }}
            >
              <Typography variant="body1">{t("loginClaim")}</Typography>
              <Button
                onClick={() =>
                  PubSub.publish(AppTopics.NavigationRequested, SIGNUP)
                }
                size="small"
                tabIndex={-1}
                variant="text"
              >
                {t("register")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </FormProvider>
  );
};
